import { useEffect, useState } from 'react';
import { newLandingSchema } from '../supabaseClient';

// Fetches every new_landing.words_* table once and caches the result for the
// lifetime of the page — the dataset is small (~100 rows total) and shared
// by every module that used to read from src/config/words.js.
let cachePromise = null;

function toMapByKey(rows) {
  const map = {};
  for (const row of rows) map[row.key] = row;
  return map;
}

async function loadWordsData() {
  const schema = newLandingSchema();

  const [nounsRes, verbsRes, verbTasksRes, generalizationsRes, antonymsRes, storiesRes] = await Promise.all([
    schema.from('words_nouns').select('*'),
    schema.from('words_verbs').select('*'),
    schema.from('words_verb_tasks').select('*, verb:words_verbs(key, conjugation)').order('sort_order'),
    schema.from('words_generalizations').select('*'),
    schema.from('words_antonyms').select('*'),
    schema.from('words_stories').select('*'),
  ]);

  for (const res of [nounsRes, verbsRes, verbTasksRes, generalizationsRes, antonymsRes, storiesRes]) {
    if (res.error) throw new Error(res.error.message);
  }

  const verbTasksBySet = {};
  for (const task of verbTasksRes.data) {
    if (!verbTasksBySet[task.task_set]) verbTasksBySet[task.task_set] = [];
    verbTasksBySet[task.task_set].push({
      prompt: task.prompt,
      promptAudioUrl: task.prompt_audio_url ?? null,
      answer: task.answer,
      tense: task.tense,
      number: task.number,
      person: task.person,
      image: task.image,
      verbKey: task.verb?.key ?? null,
      verbConjugation: task.verb?.conjugation ?? null,
    });
  }

  return {
    nouns: toMapByKey(nounsRes.data),
    verbs: toMapByKey(verbsRes.data),
    verbTasksBySet,
    generalizations: toMapByKey(generalizationsRes.data),
    antonyms: toMapByKey(antonymsRes.data),
    stories: toMapByKey(storiesRes.data),
  };
}

export function useWordsData() {
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!cachePromise) cachePromise = loadWordsData();

    cachePromise
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: null, loading: false, error });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
